import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';

// Canales disponibles
const CHANNELS = [
  { id: '922986590894796', name: 'Comercial', icon: '🏢', color: '#0078FF' },
  { id: '999551239899325', name: 'Admin', icon: '👔', color: '#6C47FF' },
  { id: '916595048209652', name: 'RR.II.', icon: '🌎', color: '#00B4D8' },
  { id: '1020657397786989', name: 'Becarios', icon: '📈', color: '#00C896' },
  { id: '983548531506198', name: 'Regulares', icon: '⭐', color: '#FFB300' },
  { id: '1308381959018456', name: 'Peru', icon: '🇵🇪', color: '#FF4081' },
  { id: '1000790306449643', name: 'Info 2', icon: 'ℹ️', color: '#FF6B6B' },
  { id: '1233806963139486', name: 'Académico', icon: '🎓', color: '#9B59B6' },
  { id: '1232012809998778', name: 'Logística', icon: '📦', color: '#E67E22' },
  { id: '1348031501732308', name: 'ONG', icon: '🤝', color: '#10B981' },
];

function useOutsideClick(ref, callback) {
  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) callback();
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [ref, callback]);
}

// Subcomponente Memoizado para cada conversación
const ConversationItem = React.memo(({
  conv, isActive, isSelectionMode, isSelected, onConversationClick, formatTime, getInitial
}) => {
  return (
    <div
      className={`conversation-item ${isActive ? 'active' : ''}`}
      onClick={() => onConversationClick(conv)}
    >
      {isSelectionMode && (
        <div className="checkbox-wrapper" style={{ marginRight: '10px' }}>
          <input type="checkbox" checked={isSelected} readOnly style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
        </div>
      )}
      <div className="conversation-avatar">{getInitial(conv.name)}</div>
      <div className="conversation-info">
        <div className="conversation-name">{conv.name}</div>
        <div className="conversation-last-message" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {conv.last_message ? (
            <>
              {conv.last_message_direction === 'outgoing' && (
                <span style={{ display: 'flex', alignItems: 'center', color: '#53bdeb', fontSize: '13px' }}>✓✓ Tú:</span>
              )}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {(() => {
                  const msg = conv.last_message || '';
                  if (msg.startsWith('/media/')) {
                    const l = msg.toLowerCase();
                    if (l.endsWith('.ogg') || l.endsWith('.mp3') || l.endsWith('.wav') || l.endsWith('.webm') || l.endsWith('.m4a') || l.endsWith('.aac')) return '🎙️ Audio';
                    if (l.endsWith('.mp4') || l.endsWith('.mov') || l.endsWith('.avi') || l.endsWith('.mkv')) return '🎥 Video';
                    if (l.endsWith('.pdf') || l.endsWith('.doc') || l.endsWith('.docx') || l.endsWith('.xls') || l.endsWith('.xlsx')) return '📄 Documento';
                    return '📷 Foto';
                  }
                  return msg;
                })()}
              </span>
            </>
          ) : 'Sin mensajes'}
        </div>
        {conv.tags && conv.tags.length > 0 && (
          <div style={{ display: 'flex', gap: '3px', marginTop: '3px' }}>
            {conv.tags.slice(0, 4).map(tag => (
              <div key={tag.id} title={tag.name} style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: tag.color }} />
            ))}
            {conv.tags.length > 4 && <span style={{ fontSize: '8px' }}>+</span>}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '5px' }}>
        {conv.last_message_at && <div className="conversation-time">{formatTime(conv.last_message_at)}</div>}
        {conv.unread_messages > 0 && (
          <div style={{ backgroundColor: '#25D366', color: 'white', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 'bold' }}>
            {conv.unread_messages}
          </div>
        )}
      </div>
    </div>
  );
});

function Sidebar({
  conversations, selectedConversation, onSelectConversation, loading,
  onLoadMore, hasMore, loadingMore,
  tags = [], filterTag: filterTagProp, setFilterTag: setFilterTagProp,
  filterChannel: filterChannelProp, onChannelChange,
  searchTerm: searchTermProp, onSearchChange,
  onOpenTagSelector, onOpenBroadcast, onOpenBroadcastFromSheet,
  onOpenSendMessageToSelected, onMarkAllAsRead,
  onOpenQuickReplyModal, onOpenAutoReplyModal,
  user, onLogout, theme = 'light', onToggleTheme,
  globalBotEnabled, onToggleGlobalBot, isMobile
}) {
  const handleScroll = (e) => {
    if (!onLoadMore || !hasMore || loadingMore) return;
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 150) {
      onLoadMore();
    }
  };
  const [searchTermLocal, setSearchTermLocal] = useState('');
  const searchTerm = searchTermProp !== undefined ? searchTermProp : searchTermLocal;
  const setSearchTerm = (term) => {
    if (onSearchChange) {
      onSearchChange(term);
    } else {
      setSearchTermLocal(term);
    }
  };

  const [filterTagLocal, setFilterTagLocal] = useState('');
  const filterTag = filterTagProp !== undefined ? filterTagProp : filterTagLocal;
  const setFilterTag = filterTagProp !== undefined ? setFilterTagProp : setFilterTagLocal;
  
  const [filterChannelLocal, setFilterChannelLocal] = useState('');
  const filterChannel = filterChannelProp !== undefined ? filterChannelProp : filterChannelLocal;
  const setFilterChannel = (ch) => {
    if (onChannelChange) {
      onChannelChange(ch);
    } else {
      setFilterChannelLocal(ch);
    }
  };
  const [filterUnread, setFilterUnread] = useState(false);

  // Selección múltiple
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showMenu, setShowMenu] = useState(false);

  // Dropdowns
  const [showChannelDrop, setShowChannelDrop] = useState(false);
  const [showTagDrop, setShowTagDrop] = useState(false);
  const [tagSearch, setTagSearch] = useState('');

  const channelRef = useRef(null);
  const tagRef = useRef(null);
  const menuRef = useRef(null);

  useOutsideClick(channelRef, () => setShowChannelDrop(false));
  useOutsideClick(tagRef, () => { setShowTagDrop(false); setTagSearch(''); });
  useOutsideClick(menuRef, () => setShowMenu(false));

  const formatTime = useCallback((dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    const tz = 'America/La_Paz';
    const optionsDate = { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' };
    const optionsTime = { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false };

    const now = new Date();
    const isToday = date.toLocaleDateString('es-ES', optionsDate) === now.toLocaleDateString('es-ES', optionsDate);

    const yesterday = new Date(now.getTime() - 86400000);
    const isYesterday = date.toLocaleDateString('es-ES', optionsDate) === yesterday.toLocaleDateString('es-ES', optionsDate);

    const timeStr = date.toLocaleTimeString('es-ES', optionsTime);

    if (isToday) return timeStr;
    if (isYesterday) return 'Ayer';
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit', timeZone: tz });
  }, []);

  const getInitial = useCallback((name) => name ? name.charAt(0).toUpperCase() : '?', []);

  const handleConversationClick = useCallback((conv) => {
    if (isSelectionMode) {
      setSelectedIds(prev => {
        const newSet = new Set(prev);
        if (newSet.has(conv.id)) newSet.delete(conv.id);
        else newSet.add(conv.id);
        return newSet;
      });
    } else {
      if (onSelectConversation) {
        onSelectConversation(conv);
      }
    }
  }, [isSelectionMode, onSelectConversation]);

  // Canales accesibles optimizados
  const accessibleChannels = useMemo(() =>
    CHANNELS.filter(ch => {
      if (!user?.channelId) return true;
      if (user?.username === 'comercial') return ['922986590894796', '1020657397786989', '1308381959018456', '1000790306449643', '1233806963139486'].includes(ch.id);
      return ch.id === user?.channelId;
    }),
    [user?.channelId, user?.username]
  );

  const activeChannel = useMemo(() => CHANNELS.find(c => c.id === filterChannel) || null, [filterChannel]);
  const activeTag = useMemo(() => tags.find(t => String(t.id) === filterTag) || null, [tags, filterTag]);

  // Contadores optimizados
  const counters = useMemo(() => {
    const uc = {};
    const ut = {};
    const ct = {};
    const safeTags = Array.isArray(tags) ? tags : [];
    const safeConversations = Array.isArray(conversations) ? conversations : [];

    safeTags.forEach(t => { if (t && t.id) { ut[t.id] = 0; ct[t.id] = 0; } });
    let tu = 0;

    safeConversations.forEach(c => {
      if (!c) return;
      const unread = parseInt(c.unread_messages, 10) || 0;
      tu += unread;
      if (unread > 0) uc[c.channel_id] = (uc[c.channel_id] || 0) + unread;
      (Array.isArray(c.tags) ? c.tags : []).forEach(t => {
        if (t && t.id) {
          ct[t.id] = (ct[t.id] || 0) + 1;
          if (unread > 0) ut[t.id] = (ut[t.id] || 0) + unread;
        }
      });
    });
    return { unreadByChannel: uc, unreadByTag: ut, countByTag: ct, totalUnread: tu };
  }, [conversations, tags]);

  const { unreadByChannel, unreadByTag, countByTag, totalUnread } = counters;

  const filteredConversations = useMemo(() => {
    const s = searchTerm.toLowerCase();
    const safeConversations = Array.isArray(conversations) ? conversations : [];
    return safeConversations.filter(conv => {
      if (!conv) return false;
      const matchSearch = !s ||
        (onSearchChange !== undefined) || // Si la búsqueda se procesa en el servidor, no filtrar localmente los resultados ya devueltos
        (conv.name && conv.name.toLowerCase().includes(s)) ||
        (conv.wa_id && conv.wa_id.includes(searchTerm)) ||
        (conv.last_message && conv.last_message.toLowerCase().includes(s));
      const matchTag = filterTag ? (Array.isArray(conv.tags) && conv.tags.some(t => String(t.id) === String(filterTag))) : true;
      const matchCh = filterChannel ? String(conv.channel_id) === String(filterChannel) : true;
      const matchUnread = filterUnread ? (parseInt(conv.unread_messages, 10) || 0) > 0 : true;
      return matchSearch && matchTag && matchCh && matchUnread;
    });
  }, [conversations, searchTerm, filterTag, filterChannel, filterUnread]);

  const filteredTagsInDrop = useMemo(() => {
    const safeTags = Array.isArray(tags) ? tags : [];
    return safeTags
      .filter(t => t && t.name && (!tagSearch || t.name.toLowerCase().includes(tagSearch.toLowerCase())))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [tags, tagSearch]);

  const isDark = theme === 'dark';
  const dropBg = isDark ? 'rgba(22, 33, 62, 0.95)' : 'rgba(255, 255, 255, 0.98)';
  const dropBorder = isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)';
  const dropShadow = isDark ? '0 20px 40px rgba(0,0,0,0.4)' : '0 20px 40px rgba(0,0,0,0.12)';
  const itemBorder = isDark ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.03)';
  const itemHover = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)';
  const textPri = isDark ? '#e9edef' : '#111b21';
  const textSec = isDark ? '#a0aab2' : '#54656f';

  if (loading && conversations.length === 0) {
    return (
      <div className="sidebar">
        <div className="sidebar-header"><h2>WhatsApp CRM</h2></div>
        <div className="loading-container"><p>Cargando conversaciones...</p></div>
      </div>
    );
  }

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        {/* HEADER TOP */}
        <div className="header-top">
          {!isSelectionMode ? (
            <>
              <h2>CRM <span style={{ fontSize: '12px', color: '#666' }}>({user?.username})</span></h2>
              <div className="header-actions">
                <button className="icon-btn" title="Seleccionar varios" onClick={() => setIsSelectionMode(true)}>
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>
                </button>
                <div style={{ position: 'relative' }} ref={menuRef}>
                  <button className="icon-btn" title="Menú" onClick={() => setShowMenu(!showMenu)}>
                    <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1" /><circle cx="12" cy="5" r="1" /><circle cx="12" cy="19" r="1" /></svg>
                  </button>
                  {showMenu && (
                    <div className="dropdown-menu" style={{ right: 0 }}>
                      <button onClick={() => { onOpenBroadcast({ selectedIds: filteredConversations.map(c => c.id) }); setShowMenu(false); }}>
                        📢 Difusión a Todos ({filteredConversations.length})
                      </button>
                      {onOpenBroadcastFromSheet && (
                        <button onClick={() => { onOpenBroadcastFromSheet(); setShowMenu(false); }}>📊 Envío masivo desde Google Sheets</button>
                      )}
                      {onMarkAllAsRead && (() => {
                        const chId = filterChannel || user?.channelId || null;
                        const unreadToMark = chId ? (unreadByChannel[chId] || 0) : 0;
                        return (
                          <button
                            className={chId ? 'dropdown-menu-item-accent' : ''}
                            onClick={() => { if (!chId) return; onMarkAllAsRead(chId); setShowMenu(false); }}
                            disabled={!chId}
                          >
                            ✓ Marcar como leídas {unreadToMark > 0 ? `(${unreadToMark})` : ''}
                          </button>
                        );
                      })()}
                      {onToggleTheme && (
                        <button onClick={() => { onToggleTheme(); setShowMenu(false); }}>
                          {isDark ? '☀️ Modo claro' : '🌙 Modo oscuro'}
                        </button>
                      )}
                      <button onClick={() => { onOpenTagSelector({ mode: 'manage' }); setShowMenu(false); }}>🏷️ Gestionar Etiquetas</button>
                      {onOpenQuickReplyModal && (
                        <button className="dropdown-menu-item-accent" onClick={() => { onOpenQuickReplyModal(); setShowMenu(false); }}>⚡ Respuestas rápidas</button>
                      )}
                      {onOpenAutoReplyModal && (
                        <button onClick={() => { onOpenAutoReplyModal(); setShowMenu(false); }}>🤖 Mensajes automáticos</button>
                      )}
                      <hr className="dropdown-menu-hr" />
                      <button className="dropdown-menu-item-logout" onClick={onLogout}>🚪 Cerrar Sesión</button>
                    </div>
                  )}
                </div>
                {/* BOTÓN TOGGLE GLOBAL IA */}
                <button 
                  className={`icon-btn ai-toggle-btn ${globalBotEnabled ? 'active' : 'inactive'}`} 
                  title={globalBotEnabled === undefined ? "Cargando..." : (globalBotEnabled ? "IA Global: ACTIVA" : "IA Global: DESACTIVADA")}
                  onClick={(e) => { e.stopPropagation(); if (globalBotEnabled !== undefined) onToggleGlobalBot && onToggleGlobalBot(); }}
                  style={{ 
                    position: 'relative',
                    background: globalBotEnabled === undefined 
                      ? 'rgba(0,0,0,0.1)' 
                      : (globalBotEnabled 
                        ? 'linear-gradient(135deg, #00a884 0%, #00ca9d 100%)' 
                        : 'linear-gradient(135deg, #667781 0%, #8696a0 100%)'),
                    color: globalBotEnabled === undefined ? '#999' : '#fff',
                    borderRadius: '20px',
                    padding: '6px 14px',
                    width: 'auto',
                    height: '38px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    border: 'none',
                    boxShadow: globalBotEnabled === true 
                      ? '0 4px 12px rgba(0, 168, 132, 0.3)' 
                      : '0 2px 6px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    cursor: globalBotEnabled === undefined ? 'default' : 'pointer',
                    transform: 'scale(1)',
                    opacity: globalBotEnabled === undefined ? 0.6 : 1
                  }}
                  onMouseEnter={(e) => { if (globalBotEnabled !== undefined) { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.filter = 'brightness(1.1)'; } }}
                  onMouseLeave={(e) => { if (globalBotEnabled !== undefined) { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.filter = 'brightness(1)'; } }}
                >
                  <span style={{ fontSize: '20px', filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.2))' }}>🤖</span>
                  <span style={{ 
                    fontSize: '12px', 
                    fontWeight: '800',
                    letterSpacing: '0.5px',
                    display: isMobile ? 'none' : 'block',
                    textTransform: 'uppercase'
                  }}>
                    IA {globalBotEnabled === undefined ? '...' : (globalBotEnabled ? 'ON' : 'OFF')}
                  </span>
                  {globalBotEnabled !== undefined && (
                    <span style={{
                      position: 'absolute',
                      top: '-2px',
                      right: '-2px',
                      width: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      background: globalBotEnabled ? '#00e676' : '#ff1744',
                      border: '2.5px solid #fff',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
                      animation: globalBotEnabled ? 'pulse-green 2s infinite' : 'none'
                    }}></span>
                  )}
                </button>
              </div>
            </>
          ) : (
            <div className="selection-header" style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '10px', flexWrap: 'wrap' }}>
              <button className="icon-btn" onClick={() => { setIsSelectionMode(false); setSelectedIds(new Set()); }}>
                <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
              <span style={{ fontWeight: '600', flex: '1 1 auto', minWidth: '90px' }}>{selectedIds.size} seleccionados</span>
              <button className="selection-btn" onClick={() => setSelectedIds(new Set(filteredConversations.map(c => c.id)))}>Todos</button>
              <button className="selection-btn" onClick={() => setSelectedIds(new Set())}>Ninguno</button>
              {selectedIds.size > 0 && (
                <div style={{ display: 'flex', gap: '5px' }}>
                  <button className="icon-btn" onClick={() => { onOpenTagSelector({ mode: 'bulk', selectedIds: Array.from(selectedIds) }); setIsSelectionMode(false); setSelectedIds(new Set()); }}>🏷️</button>
                  {onOpenSendMessageToSelected && (
                    <button className="icon-btn" title="Enviar mensaje" onClick={() => { onOpenSendMessageToSelected({ selectedIds: Array.from(selectedIds) }); setIsSelectionMode(false); setSelectedIds(new Set()); }}>💬</button>
                  )}
                  <button className="icon-btn" onClick={() => { onOpenBroadcast({ selectedIds: Array.from(selectedIds) }); setIsSelectionMode(false); setSelectedIds(new Set()); }}>📢</button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* BUSCADOR */}
        <div className="search-container">
          <div className="search-wrapper">
            <svg viewBox="0 0 24 24" width="20" height="20" stroke="#54656f" strokeWidth="2" fill="none" className="search-icon"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            <input type="text" placeholder="Buscar..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="sidebar-search" />
          </div>
        </div>

        {/* BARRA DE FILTROS PREMIUM (WRAP - FIX CORTADO) */}
        <div style={{ padding: '8px 12px 10px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          
          {/* Todos */}
          <button onClick={() => { setFilterChannel(''); setFilterTag(''); setFilterUnread(false); }}
            style={{
              padding: '6px 14px', borderRadius: '12px', border: 'none', cursor: 'pointer',
              fontSize: '13px', fontWeight: 600, transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)', flexShrink: 0,
              display: 'flex', alignItems: 'center', gap: '6px',
              background: !filterChannel && !filterTag && !filterUnread ? 'linear-gradient(135deg, #00a884 0%, #008f6f 100%)' : '#f0f2f5',
              color: !filterChannel && !filterTag && !filterUnread ? '#fff' : '#54656f',
              boxShadow: !filterChannel && !filterTag && !filterUnread ? '0 4px 12px rgba(0,168,132,0.35)' : 'none',
              transform: !filterChannel && !filterTag && !filterUnread ? 'translateY(-1px)' : 'none'
            }}>
            Todos{totalUnread > 0 && (
              <span style={{
                background: 'rgba(255,255,255,0.25)', borderRadius: '20px', padding: '2px 8px',
                fontSize: '11px', fontWeight: 700, backdropFilter: 'blur(4px)',
                boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.2)'
              }}>{totalUnread}</span>
            )}
          </button>

          {/* No leídos */}
          <button onClick={() => setFilterUnread(!filterUnread)}
            style={{
              padding: '6px 12px', borderRadius: '12px', border: '1px solid transparent', cursor: 'pointer',
              fontSize: '13px', fontWeight: 600, transition: 'all 0.2s', flexShrink: 0,
              background: filterUnread ? '#e8f5e9' : '#f0f2f5',
              color: filterUnread ? '#2e7d32' : '#54656f',
              borderColor: filterUnread ? '#c8e6c9' : 'transparent',
              boxShadow: filterUnread ? '0 4px 12px rgba(46, 125, 50, 0.15)' : 'none',
              transform: filterUnread ? 'translateY(-1px)' : 'none'
            }} title="Mostrar solo no leídos">
            📩 No leídos
          </button>

          {/* ---- DROPDOWN CANALES ---- */}
          {accessibleChannels.length > 1 && (
            <div ref={channelRef} style={{ position: 'relative', flexShrink: 0 }}>
              <button
                onClick={() => { setShowChannelDrop(v => !v); setShowTagDrop(false); }}
                style={{
                  padding: '6px 14px', borderRadius: '12px', cursor: 'pointer', flexShrink: 0,
                  fontSize: '13px', fontWeight: 600, transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap',
                  border: activeChannel ? `1px solid ${activeChannel.color}80` : '1px solid #dde1e4',
                  background: activeChannel ? `linear-gradient(135deg, ${activeChannel.color}15 0%, ${activeChannel.color}05 100%)` : '#f7f8f9',
                  color: activeChannel ? activeChannel.color : '#54656f',
                  boxShadow: activeChannel ? `0 4px 12px ${activeChannel.color}25` : 'none',
                  transform: activeChannel ? 'translateY(-1px)' : 'none'
                }}>
                {activeChannel ? <>{activeChannel.icon} {activeChannel.name}</> : <>📡 Canal</>}
                <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"
                  style={{ transform: showChannelDrop ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {showChannelDrop && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 10px)', left: 0, zIndex: 1000,
                  background: dropBg, backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
                  borderRadius: '16px', minWidth: '220px',
                  boxShadow: dropShadow, overflow: 'hidden',
                  animation: 'dropFadeIn 0.2s cubic-bezier(0.16, 1, 0.3, 1)', border: dropBorder
                }}>
                  <div onClick={() => { setFilterChannel(''); setShowChannelDrop(false); }}
                    style={{
                      padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px',
                      background: !filterChannel ? 'rgba(0,168,132,0.15)' : 'transparent',
                      borderBottom: itemBorder, transition: 'background 0.2s'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = !filterChannel ? 'rgba(0,168,132,0.2)' : itemHover}
                    onMouseLeave={e => e.currentTarget.style.background = !filterChannel ? 'rgba(0,168,132,0.15)' : 'transparent'}>
                    <div style={{ width: 36, height: 36, borderRadius: '12px', background: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>🌐</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: textPri }}>Todos los canales</div>
                    </div>
                  </div>
                  {accessibleChannels.map(ch => {
                    const isAct = filterChannel === ch.id;
                    return (
                      <div key={ch.id}
                        onClick={() => { setFilterChannel(isAct ? '' : ch.id); setShowChannelDrop(false); }}
                        style={{
                          padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px',
                          background: isAct ? `${ch.color}20` : 'transparent',
                          borderBottom: itemBorder, transition: 'background 0.2s'
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = isAct ? `${ch.color}25` : itemHover}
                        onMouseLeave={e => e.currentTarget.style.background = isAct ? `${ch.color}20` : 'transparent'}>
                        <div style={{
                          width: 36, height: 36, borderRadius: '12px',
                          background: `${ch.color}20`, border: `1px solid ${ch.color}40`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px'
                        }}>{ch.icon}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '14px', fontWeight: 600, color: isAct ? ch.color : textPri }}>{ch.name}</div>
                        </div>
                        {unreadByChannel[ch.id] > 0 && (
                          <span style={{ 
                            background: '#25D366', 
                            color: '#fff', 
                            borderRadius: '12px', 
                            padding: '2px 8px', 
                            fontSize: '11px', 
                            fontWeight: 800, 
                            boxShadow: '0 2px 5px rgba(37,211,102,0.3)' 
                          }}>
                            {unreadByChannel[ch.id]}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ---- DROPDOWN ETIQUETAS ---- */}
          <div ref={tagRef} style={{ position: 'relative', flexShrink: 0 }}>
            <button
              onClick={() => { setShowTagDrop(v => !v); setShowChannelDrop(false); }}
              style={{
                padding: '6px 14px', borderRadius: '12px', cursor: 'pointer', flexShrink: 0,
                fontSize: '13px', fontWeight: 600, transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap',
                border: activeTag ? `1px solid ${activeTag.color}80` : '1px solid #dde1e4',
                background: activeTag ? `linear-gradient(135deg, ${activeTag.color}15 0%, ${activeTag.color}05 100%)` : '#f7f8f9',
                color: activeTag ? activeTag.color : '#54656f',
                boxShadow: activeTag ? `0 4px 12px ${activeTag.color}25` : 'none',
                transform: activeTag ? 'translateY(-1px)' : 'none'
              }}>
              🏷️ {activeTag ? activeTag.name : 'Etiquetas'}
              <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"
                style={{ transform: showTagDrop ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {showTagDrop && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 10px)', left: 0, zIndex: 1000,
                background: dropBg, backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
                borderRadius: '16px', minWidth: '240px', maxWidth: '280px',
                boxShadow: dropShadow, overflow: 'hidden',
                animation: 'dropFadeIn 0.2s cubic-bezier(0.16, 1, 0.3, 1)', border: dropBorder
              }}>
                {/* Buscador */}
                <div style={{ padding: '12px 14px', borderBottom: itemBorder }}>
                  <input type="text" placeholder="Buscar etiqueta..." value={tagSearch}
                    onChange={e => setTagSearch(e.target.value)} autoFocus
                    style={{
                      width: '100%', boxSizing: 'border-box', padding: '8px 10px',
                      background: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)', border: itemBorder,
                      borderRadius: '10px', color: textPri, fontSize: '13px', outline: 'none'
                    }}
                  />
                </div>
                <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                  <div onClick={() => { setFilterTag(''); setShowTagDrop(false); setTagSearch(''); }}
                    style={{
                      padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px',
                      background: !filterTag ? 'rgba(0,168,132,0.15)' : 'transparent',
                      borderBottom: itemBorder, transition: 'background 0.2s'
                    }}>
                    <span style={{ fontSize: '14px', fontWeight: 600, color: textPri }}>Todas las etiquetas</span>
                  </div>
                  {filteredTagsInDrop.map(tag => {
                    const isAct = filterTag === String(tag.id);
                    return (
                      <div key={tag.id}
                        onClick={() => { setFilterTag(isAct ? '' : String(tag.id)); setShowTagDrop(false); setTagSearch(''); }}
                        style={{
                          padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px',
                          background: isAct ? `${tag.color}20` : 'transparent',
                          borderBottom: itemBorder, transition: 'background 0.2s'
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = isAct ? `${tag.color}25` : itemHover}
                        onMouseLeave={e => e.currentTarget.style.background = isAct ? `${tag.color}20` : 'transparent'}>
                        <div style={{ width: 14, height: 14, borderRadius: '4px', background: tag.color, flexShrink: 0 }} />
                        <span style={{ fontSize: '14px', fontWeight: 500, color: isAct ? tag.color : textPri, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {tag.name}
                        </span>
                        <span style={{ fontSize: '12px', color: textSec, flexShrink: 0, background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', padding: '2px 6px', borderRadius: '8px' }}>
                          {tag.conversation_count !== undefined ? tag.conversation_count : (countByTag[tag.id] || 0)}
                        </span>
                        {unreadByTag[tag.id] > 0 && (
                          <span style={{ 
                            background: '#25D366', 
                            color: '#fff', 
                            borderRadius: '12px', 
                            padding: '2px 8px', 
                            fontSize: '11px', 
                            fontWeight: 800, 
                            boxShadow: '0 2px 5px rgba(37,211,102,0.3)' 
                          }}>
                            {unreadByTag[tag.id]}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div style={{ borderTop: itemBorder, padding: '12px' }}>
                  <button onClick={() => { onOpenTagSelector({ mode: 'manage' }); setShowTagDrop(false); setTagSearch(''); }}
                    style={{
                      width: '100%', padding: '10px', borderRadius: '10px',
                      background: 'rgba(0,168,132,0.1)', color: '#00a884', cursor: 'pointer',
                      fontSize: '13px', fontWeight: 600, border: 'none'
                    }}>
                    🏷️ Gestionar Etiquetas
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* BADGES DE FILTRO ACTIVO */}
        {(activeChannel || activeTag || filterUnread) && (
          <div style={{ padding: '0 12px 10px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {activeChannel && (
              <span style={{ background: `${activeChannel.color}15`, color: activeChannel.color, padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, border: `1px solid ${activeChannel.color}30` }}>
                {activeChannel.icon} {activeChannel.name}
                <button onClick={() => setFilterChannel('')} style={{ background: 'none', border: 'none', marginLeft: '6px', color: activeChannel.color, cursor: 'pointer', fontWeight: 'bold' }}>×</button>
              </span>
            )}
            {activeTag && (
              <span style={{ background: `${activeTag.color}15`, color: activeTag.color, padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, border: `1px solid ${activeTag.color}30` }}>
                🏷️ {activeTag.name}
                <button onClick={() => setFilterTag('')} style={{ background: 'none', border: 'none', marginLeft: '6px', color: activeTag.color, cursor: 'pointer', fontWeight: 'bold' }}>×</button>
              </span>
            )}
            {filterUnread && (
              <span style={{ background: '#e8f5e9', color: '#2e7d32', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, border: '1px solid #c8e6c9' }}>
                📩 No leídos
                <button onClick={() => setFilterUnread(false)} style={{ background: 'none', border: 'none', marginLeft: '6px', color: '#2e7d32', cursor: 'pointer', fontWeight: 'bold' }}>×</button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* LISTA DE CONVERSACIONES */}
      <div className="conversations-list" onScroll={handleScroll}>
        {filteredConversations.length === 0 ? (
          <div className="loading-container"><p>{searchTerm ? 'No hay resultados' : 'No hay conversaciones'}</p></div>
        ) : (
          <>
            {filteredConversations.map(conv => (
              <ConversationItem
                key={conv.id}
                conv={conv}
                isActive={selectedConversation?.id === conv.id}
                isSelectionMode={isSelectionMode}
                isSelected={selectedIds.has(conv.id)}
                onConversationClick={handleConversationClick}
                formatTime={formatTime}
                getInitial={getInitial}
              />
            ))}
            {loadingMore && (
              <div style={{ padding: '12px', textAlign: 'center', color: '#888', fontSize: '13px' }}>
                ⌛ Cargando más conversaciones...
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        @keyframes dropFadeIn {
          from { opacity: 0; transform: translateY(-8px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}

export default Sidebar;
